import React, { useEffect, useState } from 'react';
import { UsersIcon, CalendarIcon } from 'lucide-react';
import SearchBar from './SearchBar';
import EventCard from './EventCard';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { EventCategoryConfig, SampleEvent } from '../../data/eventCategoryData';
interface EventCategoryPageProps {
  config: EventCategoryConfig;
}
export function EventCategoryPage({
  config
}: EventCategoryPageProps) {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [locationSearch, setLocationSearch] = useState('');
  const [savedEvents, setSavedEvents] = useState<number[]>([]);
  useEffect(() => {
    const saved = localStorage.getItem('savedEvents');
    if (saved) {
      try {
        setSavedEvents(JSON.parse(saved));
      } catch {
        // ignore parse errors
      }
    }
  }, []);
  useEffect(() => {
    localStorage.setItem('savedEvents', JSON.stringify(savedEvents));
  }, [savedEvents]);
  const handleToggleSave = (eventId: number) => {
    setSavedEvents(prev => prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]);
  };
  const filteredEvents = config.sampleEvents.filter(event => !locationSearch || event.location.toLowerCase().includes(locationSearch.toLowerCase()));
  return <div className={`min-h-screen ${dk.page} py-8`} data-id="element-1565" data-mp="7lc7m9520kh">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1566" data-mp="xvve818xcobl">
        <div className="text-center mb-8" data-id="element-1567" data-mp="syd4i8n3252j">
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-1568" data-mp="symu3v47ua7p">
            {config.title}
          </h1>
          <p className={`mt-2 text-lg ${dk.textSecondary}`} data-id="element-1569" data-mp="nx5u01tk2rs6">
            {config.subtitle}
          </p>
        </div>

        <SearchBar locationSearch={locationSearch} setLocationSearch={setLocationSearch} placeholder={config.searchPlaceholder} data-id="element-1570" data-mp="j2a00w4rducf" />

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-1571" data-mp="33olbpyoz52wm">
          {filteredEvents.map(event => <EventCard key={event.id} title={event.title} date={event.date} time={event.time} type={event.type} description={event.description} image={event.image} location={event.location} link={event.link || `/events/${event.id}`} eventId={event.id} isSaved={savedEvents.includes(event.id)} onToggleSave={handleToggleSave} status={event.status} price={event.price || (event.ticketPrice ? `${event.ticketPrice}` : undefined)} isVirtual={event.isVirtual} country={event.country} data-id="element-1572" data-mp="1vrh4gu9ks5ib">
              {event.author && <div className={`text-sm font-medium mb-2 ${dk.textPrimary}`} data-id="element-1573" data-mp="t4mat48ls6ed">
                  by {event.author}
                </div>}

              <div className={`flex items-center text-xs ${dk.textMuted}`} data-id="element-1574" data-mp="egdnmqqd8fw6">
                <UsersIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-1575" data-mp="aftqhn5slca1" />
                {event.attendees} {config.attendeeLabel} •{' '}
                {event.maxAttendees - event.attendees} spots left
              </div>

              {event.isFree ? <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-1576" data-mp="jfgumgpwow6s">
                  <span className={`inline-block bg-green-100 text-green-800 text-xs px-2.5 py-1 ${rt.badgeRadius} font-medium`} data-id="element-1577" data-mp="xeycsd8p5b76">
                    Free Event
                  </span>
                </div> : event.includesBook && event.ticketPrice ? <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-1578" data-mp="12816cldh4knd">
                  <div className="flex flex-col" data-id="element-1579" data-mp="1h1wbphef7rtt">
                    <span className={`font-bold ${dk.textPrimary}`} data-id="element-1580" data-mp="1c5p47k6dm89r">
                      ${event.ticketPrice}
                    </span>
                    <span className={`text-[10px] ${dk.textMuted}`} data-id="element-1581" data-mp="35ncy08rqesxr">
                      Includes book copy
                    </span>
                  </div>
                </div> : null}
            </EventCard>)}
        </div>

        {filteredEvents.length === 0 && <div className="text-center py-12" data-id="element-1582" data-mp="3kbr9f837rppx">
            <CalendarIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-1583" data-mp="2mp0oun0tlj26" />
            <p className={`text-lg ${dk.textMuted}`} data-id="element-1584" data-mp="2rnbwcr3n7hh8">{config.emptyMessage}</p>
          </div>}
      </div>
    </div>;
}